import React from 'react';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'danger' | 'warning' | 'info';
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
  children,
  onClick,
  ...props
}) => {
  const variantClasses = {
    default:
      'bg-vb-surface-hover text-vb-text-primary border border-vb-border dark:bg-vb-surface-hover-dark dark:text-vb-text-primary-dark dark:border-vb-border-dark',
    success: 'bg-vb-success-light text-green-900 dark:bg-vb-success-light-dark dark:text-green-100',
    danger: 'bg-vb-danger-light text-red-900 dark:bg-vb-danger-light-dark dark:text-red-100',
    warning:
      'bg-vb-warning-light text-yellow-900 dark:bg-vb-warning-light-dark dark:text-yellow-100',
    info: 'bg-vb-primary-light text-blue-900 dark:bg-vb-primary-light-dark dark:text-blue-100'
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const dotColorClasses = {
    default: 'bg-vb-text-secondary dark:bg-vb-text-secondary-dark',
    success: 'bg-vb-success',
    danger: 'bg-vb-danger',
    warning: 'bg-vb-warning',
    info: 'bg-vb-primary'
  };

  // When onClick is provided, add interactive accessibility attributes
  const isClickable = !!onClick;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>);
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      {...props}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColorClasses[variant]}`} />}
      {children}
    </span>
  );
};
